import { supabase } from '@/integrations/supabase/client';

// Standard query structure for fetching listings with all related data
export const STANDARD_LISTING_QUERY = `
  *,
  helicopters!helicopter_listings_helicopter_id_fkey (*),
  profiles!fk_helicopter_listings_user_id (
    display_name,
    avatar_url,
    user_type
  )
`;

export interface StandardizedListing {
  id: string;
  user_id: string;
  title: string;
  price_usd?: number;
  budget_min?: number;
  budget_max?: number;
  listing_type: string;
  lease_type?: string;
  location?: string;
  latitude?: number;
  longitude?: number;
  year_manufactured?: number;
  total_flight_hours?: number;
  condition?: string;
  featured: boolean;
  images?: string[];
  status: string;
  created_at: string;
  operational_requirements?: any;
  is_buy_request?: boolean;
  // Phase 1: New fields
  engine_cycles?: number;
  urgency_level?: string;
  down_payment?: number;
  loan_term?: number;
  purchase_type?: string;
  preferred_delivery_date?: string;
  model_preferences?: any;
  search_radius?: number;
  configuration_id?: string;
  configuration_name?: string;
  configuration_type?: string;
  helicopters: {
    id: string;
    manufacturer: string;
    model: string;
    seating: number;
    speed_kmh?: number;
    range_km?: number;
    eco_score?: number;
    icon_url?: string;
  } | null;
  profiles: {
    display_name: string;
    avatar_url?: string;
    user_type: string;
  } | null;
  listing_media?: Array<{
    id: string;
    file_url: string;
    media_type: string;
    display_order?: number;
    metadata?: any;
  }>;
  selected_options?: Array<{
    id: string;
    category: string;
    name: string;
    description?: string;
    icon_ref?: string;
    value_add_percent: number;
  }>;
}

interface FetchListingsFilters {
  status?: string | string[];
  listingTypes?: string[];
  featured?: boolean;
  userId?: string;
  limit?: number;
  excludeTypes?: string[];
  isBuyRequest?: boolean;
}

/**
 * Fetch listings with all related data using a standardized query
 */
export async function fetchStandardizedListings(
  filters: FetchListingsFilters = {}
): Promise<StandardizedListing[]> {
  try {
    // Build base query
    let query = supabase
      .from('helicopter_listings')
      .select(STANDARD_LISTING_QUERY);

    // Apply filters
    if (filters.status) {
      if (Array.isArray(filters.status)) {
        query = query.in('status', filters.status);
      } else {
        query = query.eq('status', filters.status);
      }
    }

    if (filters.listingTypes && filters.listingTypes.length > 0) {
      query = query.in('listing_type', filters.listingTypes);
    }

    if (filters.excludeTypes && filters.excludeTypes.length > 0) {
      query = query.not('listing_type', 'in', `(${filters.excludeTypes.join(',')})`);
    }

    // Filter by buy request flag
    if (filters.isBuyRequest !== undefined) {
      query = query.eq('is_buy_request', filters.isBuyRequest);
    }

    if (filters.featured !== undefined) {
      query = query.eq('featured', filters.featured);
    }

    if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }

    if (filters.limit) {
      query = query.limit(filters.limit);
    }

    // Default ordering - newest created listings first
    query = query.order('created_at', { ascending: false });

    const { data: listingsData, error: listingsError } = await query;

    if (listingsError) throw listingsError;
    if (!listingsData || listingsData.length === 0) return [];

    // Get all listing IDs to fetch media
    const listingIds = listingsData.map(l => l.id);

    // Extract selected option IDs from all listings (limit to 10 per listing, cap total at 50)
    const allSelectedOptionIds = listingsData.flatMap(listing => {
      const opReqs = listing.operational_requirements as any;
      const options = opReqs?.selectedOptions || [];
      return options.slice(0, 10); // Limit per listing
    }).filter(Boolean);
    
    // Dedupe and cap total option IDs
    const uniqueOptionIds = [...new Set(allSelectedOptionIds)].slice(0, 50);

    // Fetch media AND options in PARALLEL for better performance
    const [mediaResult, optionsResult] = await Promise.all([
      supabase
        .from('listing_media')
        .select('listing_id, id, file_url, media_type, display_order, metadata')
        .in('listing_id', listingIds)
        .eq('media_type', 'photo')
        .order('display_order', { ascending: true }),
      uniqueOptionIds.length > 0
        ? supabase.from('v_options_with_icons').select('*').in('id', uniqueOptionIds)
        : Promise.resolve({ data: [], error: null })
    ]);

    const { data: mediaData } = mediaResult;
    const { data: optionsData, error: optionsError } = optionsResult;

    if (optionsError) {
      console.warn('Error fetching options:', optionsError);
    }

    // Attach selected options and media to each listing
    const enrichedListings: StandardizedListing[] = listingsData.map(listing => {
      const selectedOptions = extractSelectedOptions(listing, optionsData || []);
      const listingMedia = mediaData?.filter(m => m.listing_id === listing.id) || [];
      
      return {
        ...(listing as any),
        selected_options: selectedOptions,
        listing_media: listingMedia
      } as StandardizedListing;
    });

    return enrichedListings;
  } catch (error) {
    console.error('Error fetching standardized listings:', error);
    throw error;
  }
}

/**
 * Extract aircraft details from operational_requirements with fallback to top-level fields
 */
export function extractAircraftDetails(listing: any): any {
  const opReqs = listing.operational_requirements;
  const details = opReqs?.aircraftDetails || {};

  return {
    year: listing.year_manufactured ?? details.year ?? null,
    condition: listing.condition ?? details.condition ?? null,
    totalFlightHours: listing.total_flight_hours ?? details.totalFlightHours ?? details.totalTimeSinceNew ?? null,
    registrationNumber: details.registrationNumber ?? null,
    serialNumber: details.serialNumber ?? null,
    engineHours: details.engineHours ?? null, 
    // Phase 1: Pull engine_cycles from top-level field (sale listings)
    engineCycles: listing.engine_cycles ?? details.engineCycles ?? null, 
    lastInspectionDate: details.lastInspectionDate ?? null,
    lastInspectionType: details.lastInspectionType ?? null,
    nextMaintenanceDate: details.nextMaintenanceDate ?? null,

    // Correct wizard field mappings with fallbacks
    paintCondition: details.paintCondition ?? details.paint ?? null,
    interiorCondition: details.interiorCondition ?? details.interior ?? null,
    avionicsCondition: details.avionicsCondition ?? details.avionics ?? null,
    engineCondition: details.engineCondition ?? details.engine ?? null,

    adsbCompliant: details.adsbCompliant ?? false,
    damageHistory: details.damageHistory ?? null,
    warranties: details.warranties ?? null,

    // Additional fields from wizard
    totalLandings: details.totalLandings ?? null,
    maintenanceHistory: details.maintenanceHistory ?? null,
    complianceStatus: details.complianceStatus ?? null,
    mainRotorLifeRemaining: details.mainRotorLifeRemaining ?? null,
    tailRotorLifeRemaining: details.tailRotorLifeRemaining ?? null,
    transmissionLifeRemaining: details.transmissionLifeRemaining ?? null,
    gpsType: details.gpsType ?? null,
    autopilotSystem: details.autopilotSystem ?? null,
    engineWarranty: details.engineWarranty ?? null,
    airframeWarranty: details.airframeWarranty ?? null,
    avionicsPackage: details.avionicsPackage ?? null,
    avionicsWarranty: details.avionicsWarranty ?? null,

    // Additional fields used in detail views
    engineType: details.engineType ?? null,
    landingGear: details.landingGear ?? null,
    
    // Component life tracking fields for progress bars/graphs
    engineLifeLimit: details.engineLifeLimit ?? null,
    engineCurrentHours: details.engineCurrentHours ?? null,
    engineLifeRemaining: details.engineLifeRemaining ?? null,
    
    mainRotorLifeLimit: details.mainRotorLifeLimit ?? null,
    mainRotorCurrentHours: details.mainRotorCurrentHours ?? null,
    
    tailRotorLifeLimit: details.tailRotorLifeLimit ?? null,
    tailRotorCurrentHours: details.tailRotorCurrentHours ?? null,
    
    transmissionLifeLimit: details.transmissionLifeLimit ?? null,
    transmissionCurrentHours: details.transmissionCurrentHours ?? null,
    
    // Interior configuration
    interiorType: details.interiorType ?? null,
    
    // Twin engine configuration
    isTwinEngine: details.isTwinEngine ?? false,
    engine1Ttsn: details.engine1Ttsn ?? null,
    engine1Hours: details.engine1Hours ?? null,
    engine1Cycles: details.engine1Cycles ?? null,
    engine2Ttsn: details.engine2Ttsn ?? null,
    engine2Hours: details.engine2Hours ?? null,
    engine2Cycles: details.engine2Cycles ?? null,
  };
}

// Extract rent-specific data
export function extractRentData(listing: any): any {
  const opReqs = listing.operational_requirements || {};
  
  return {
    // Pricing
    hourlyRate: opReqs.hourlyRate ?? listing.hourly_rate ?? null,
    dailyRate: opReqs.dailyRate ?? listing.daily_rate ?? null,
    weeklyRate: opReqs.weeklyRate ?? null,
    securityDeposit: opReqs.securityDeposit ?? listing.security_deposit ?? null,
    peakSeasonMultiplier: opReqs.peakSeasonMultiplier ?? null,
    offSeasonDiscount: opReqs.offSeasonDiscount ?? null,
    weekendPremium: opReqs.weekendPremium ?? null,
    
    // Requirements
    minimumRentalPeriod: opReqs.minimumRentalPeriod ?? null,
    pilotRequirements: opReqs.pilotRequirements ?? listing.pilot_requirements ?? null,
    insuranceRequirements: opReqs.insuranceRequirements ?? listing.insurance_requirements ?? null,
    checkoutRequirements: opReqs.checkoutRequirements ?? null,
    insuranceLiabilityMin: opReqs.insuranceLiabilityMin ?? null,
    insuranceHullMin: opReqs.insuranceHullMin ?? null,
    pilotMinimumHours: opReqs.pilotMinimumHours ?? null,
    pilotMinimumHelicopterHours: opReqs.pilotMinimumHelicopterHours ?? null,
    pilotTypeRatingRequired: opReqs.pilotTypeRatingRequired ?? null,
    pilotRecentExperience: opReqs.pilotRecentExperience ?? null,
    
    // Terms
    fuelPolicy: opReqs.fuelPolicy ?? null,
    cancellationPolicy: opReqs.cancellationPolicy ?? opReqs.detailedCancellationPolicy ?? null,
    geographicRestrictions: opReqs.geographicRestrictions ?? null,
    weatherMinimums: opReqs.weatherMinimums ?? null,
    availabilityStatus: opReqs.availabilityStatus ?? 'available',
    
    // Policies & Amenities
    rules: opReqs.rules ?? listing.rules ?? null,
    amenities: opReqs.amenities ?? listing.amenities ?? null,
  };
}

// Extract lease-specific data
export function extractLeaseData(listing: any): any {
  const opReqs = listing.operational_requirements || {};
  const dryTerms = opReqs.dryLeaseTerms || {};
  const wetTerms = opReqs.wetLeaseTerms || {};
  
  return {
    // Basic terms (from top level or lease_terms or flat opReqs)
    minimumLeasePeriod: listing.minimum_lease_period ?? opReqs.minimumLeasePeriod ?? null,
    maximumLeasePeriod: listing.maximum_lease_period ?? opReqs.maximumLeasePeriod ?? null,
    monthlyRate: opReqs.monthlyRate ?? listing.lease_terms?.monthlyRate ?? null,
    hourlyRate: opReqs.hourlyRate ?? listing.lease_terms?.hourlyRate ?? null,
    depositRequired: opReqs.depositRequired ?? listing.lease_terms?.depositRequired ?? null,
    earlyTerminationFee: opReqs.earlyTerminationFeeAmount ?? dryTerms.earlyTerminationFeeAmount ?? null,
    
    // Dry lease specific
    maintenanceResponsibility: dryTerms.maintenanceResponsibility ?? opReqs.maintenanceResponsibility ?? null,
    partsAndRepairs: dryTerms.partsAndRepairsPayment ?? opReqs.partsAndRepairsPayment ?? null,
    returnConditions: dryTerms.returnConditions ?? opReqs.returnConditions ?? null,
    maxUsageHoursPerMonth: dryTerms.maxUsageHoursPerMonth ?? opReqs.maxUsageHoursPerMonth ?? null,
    insuranceCoverageRequired: dryTerms.insuranceCoverageRequired ?? opReqs.insuranceCoverageRequired ?? null,
    registrationRequirements: dryTerms.registrationRequirements ?? opReqs.registrationRequirements ?? null,
    inspectionRights: dryTerms.inspectionRights ?? opReqs.inspectionRights ?? null,
    operationalCoverage: opReqs.operationalCoverage ?? [],
    
    // Wet lease specific
    crewQualifications: wetTerms.crewQualifications ?? opReqs.crewQualifications ?? opReqs.crewCredentials ?? null,
    standbyRates: wetTerms.standbyRates ?? opReqs.standbyRates ?? null,
    positioningCosts: wetTerms.positioningCosts ?? opReqs.positioningCosts ?? null,
    minimumBilling: wetTerms.minimumBilling ?? opReqs.minimumBilling ?? null,
    operationalControl: wetTerms.operationalControl ?? opReqs.operationalControl ?? null,
    thirdPartyOperations: wetTerms.thirdPartyOperations ?? opReqs.thirdPartyOperations ?? null,
    fuelSurchargePolicy: wetTerms.fuelSurchargePolicy ?? opReqs.fuelSurchargePolicy ?? null,
    maintenanceScopeDetailed: wetTerms.maintenanceScopeDetailed ?? opReqs.maintenanceScopeDetailed ?? null,
    crewTrainingRequirements: opReqs.crewTrainingRequirements ?? null,
    
    // Service details
    includedServices: listing.included_services || opReqs.includedServices || opReqs.selectedServices || [],
    serviceLevel: opReqs.serviceLevel ?? null,
    responseTime: opReqs.responseTime ?? null,
    
    leaseType: listing.lease_type ?? null,
  };
}

/**
 * Extract buy request-specific data from operational_requirements
 */
export function extractBuyData(listing: any): any {
  const opReqs = listing.operational_requirements || {};
  const buyDetails = opReqs.buyRequestDetails || {};
  
  return {
    // Budget & Financing
    budgetMin: listing.budget_min,
    budgetMax: listing.budget_max,
    financingStatus: listing.financing_status,
    urgencyLevel: listing.urgency_level,
    
    // Requirements
    maxAge: listing.max_age,
    maxFlightHours: listing.max_flight_hours,
    minCondition: buyDetails.minCondition || opReqs.minCondition,
    requiredEquipment: listing.required_equipment || [],
    
    // Location & Delivery
    searchRadius: listing.search_radius,
    preferredDeliveryDate: listing.preferred_delivery_date,
    
    // Contact
    contactName: listing.contact_name,
    contactPhone: listing.contact_phone,
    contactCompany: listing.contact_company,
    bestTimeToCall: listing.best_time_to_call,
    
    // Phase 1: Buy request enhancements from top-level DB fields
    downPayment: listing.down_payment ?? buyDetails.downPayment ?? null,
    loanTerm: listing.loan_term ?? buyDetails.loanTerm ?? null,
    purchaseType: listing.purchase_type ?? null,
    modelPreferences: listing.model_preferences ?? [],
    
    // Extended buy details (from operational_requirements)
    budgetFlexibility: buyDetails.budgetFlexibility,
    creditScoreRange: buyDetails.creditScoreRange,
    intendedUse: buyDetails.intendedUse,
    preApprovalAmount: buyDetails.preApprovalAmount,
    preferredDeliveryMethod: buyDetails.preferredDeliveryMethod,
    tradeInAircraft: buyDetails.tradeInAircraft,
    
    // Buy Lease-specific
    estimatedMonthlyHours: opReqs.estimatedMonthlyHours,
    operationalType: opReqs.operationalType,
    maintenancePreferences: opReqs.maintenancePreferences,
    desiredStartDate: opReqs.desiredStartDate,
    insuranceRequirements: opReqs.insuranceRequirements,
    pilotQualifications: opReqs.pilotQualifications,
    minimumAvailability: opReqs.minimumAvailability,
    backupAircraftRequired: opReqs.backupAircraftRequired,
    maintenanceResponseTime: opReqs.maintenanceResponseTime,
    crewQualificationsRequired: opReqs.crewQualificationsRequired,
    crewLanguagePreferences: opReqs.crewLanguagePreferences,
    specializedTraining: opReqs.specializedTraining,
  };
}

/**
 * Extract selected options from operational_requirements
 */
export function extractSelectedOptions(
  listing: any,
  allOptions: any[]
): any[] {
  if (!listing.operational_requirements) return [];
  
  const operationalReqs = listing.operational_requirements as any;
  const selectedOptionIds = operationalReqs?.selectedOptions || [];
  
  if (!Array.isArray(selectedOptionIds) || selectedOptionIds.length === 0) {
    return [];
  }
  
  return allOptions.filter(opt => selectedOptionIds.includes(opt.id));
}

/**
 * Extract images from listing in priority order:
 * For Buy Requests:
 * 1. hero_image if set
 * 2. listing_media (sorted by display_order)
 * 3. listing.images array
 * 4. helicopter icon_url
 * 
 * For Regular Listings:
 * 1. listing_media (sorted by display_order)
 * 2. listing.images array
 * 3. helicopter icon_url
 */
export function extractListingImages(listing: StandardizedListing): string[] {
  // First priority: listing_media sorted by display_order
  if (listing.listing_media && listing.listing_media.length > 0) {
    // Filter for photos or items without media_type (legacy data)
    const photoMedia = listing.listing_media
      .filter(media => 
        media.media_type === 'photo' || 
        !media.media_type || 
        (media.media_type !== 'video' && media.file_url)
      )
      .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
      .map(media => media.file_url)
      .filter(url => url);
    
    if (photoMedia.length > 0) {
      return photoMedia;
    }
  }

  // Second priority: legacy images array
  if (listing.images && listing.images.length > 0) {
    return listing.images.filter(url => url);
  }

  // Third priority: helicopter icon
  if (listing.helicopters?.icon_url) {
    return [listing.helicopters.icon_url];
  }

  return [];
}

/**
 * Transform a standardized listing to card format
 */
export function transformListingForCard(
  listing: StandardizedListing,
  isBuyRequest = false
): any {
  const images = extractListingImages(listing);
  const aircraftDetails = extractAircraftDetails(listing);
  const rentData = (listing.listing_type === 'rent' && !listing.is_buy_request) ? extractRentData(listing) : null;
  const leaseData = ((listing.listing_type === 'dry_lease' || listing.listing_type === 'wet_lease') && !listing.is_buy_request) 
    ? extractLeaseData(listing) 
    : null;
  const buyData = listing.is_buy_request ? extractBuyData(listing) : null;

  return {
    helicopter: listing.helicopters,
    title: listing.title || `${listing.helicopters?.manufacturer || ''} ${listing.helicopters?.model || ''}`.trim(),
    price: (listing.is_buy_request || isBuyRequest) ? (listing.budget_max || 0) : (listing.price_usd || 0),
    location: listing.location || 'Location TBD',
    condition: aircraftDetails.condition,
    year: aircraftDetails.year,
    flightHours: aircraftDetails.totalFlightHours,
    images,
    selectedOptions: listing.selected_options || [],
    isPreview: false,
    listingId: listing.id,
    sellerId: listing.user_id,
    sellerName: listing.profiles?.display_name || 'Seller',
    listingType: listing.listing_type,
    featured: listing.featured,
    is_buy_request: listing.is_buy_request,
    // Additional aircraft details
    registrationNumber: aircraftDetails.registrationNumber,
    serialNumber: aircraftDetails.serialNumber,
    engineHours: aircraftDetails.engineHours,
    engineCycles: aircraftDetails.engineCycles,
    lastInspectionDate: aircraftDetails.lastInspectionDate,
    lastInspectionType: aircraftDetails.lastInspectionType,
    nextMaintenanceDate: aircraftDetails.nextMaintenanceDate,
    paintCondition: aircraftDetails.paintCondition,
    interiorCondition: aircraftDetails.interiorCondition,
    avionicsCondition: aircraftDetails.avionicsCondition,
    engineCondition: aircraftDetails.engineCondition,
    adsbCompliant: aircraftDetails.adsbCompliant,
    damageHistory: aircraftDetails.damageHistory,
    warranties: aircraftDetails.warranties,
    // Extended wizard fields
    totalLandings: aircraftDetails.totalLandings,
    maintenanceHistory: aircraftDetails.maintenanceHistory,
    complianceStatus: aircraftDetails.complianceStatus,
    mainRotorLifeRemaining: aircraftDetails.mainRotorLifeRemaining,
    tailRotorLifeRemaining: aircraftDetails.tailRotorLifeRemaining,
    transmissionLifeRemaining: aircraftDetails.transmissionLifeRemaining,
    gpsType: aircraftDetails.gpsType,
    autopilotSystem: aircraftDetails.autopilotSystem,
    engineWarranty: aircraftDetails.engineWarranty,
    airframeWarranty: aircraftDetails.airframeWarranty,
    avionicsPackage: aircraftDetails.avionicsPackage,
    avionicsWarranty: aircraftDetails.avionicsWarranty,
    engineType: aircraftDetails.engineType,
    landingGear: aircraftDetails.landingGear,
    // Rent/Lease/Buy data
    rentData,
    leaseData,
    buyData,
    // Phase 1: Pass new fields to card
    urgencyLevel: listing.urgency_level,
    downPayment: listing.down_payment,
    loanTerm: listing.loan_term,
    purchaseType: listing.purchase_type,
    preferredDeliveryDate: listing.preferred_delivery_date,
    modelPreferences: listing.model_preferences,
    maxDistance: listing.search_radius,
    // Configuration
    configurationId: listing.configuration_id,
    configurationName: listing.configuration_name,
    configurationType: listing.configuration_type,
  };
}

